import { type Control, useFormContext, useWatch } from "react-hook-form";
import { useResourceFormContext as useFormFrameworkContext } from "src/components/Form/hooks/useResourceFormContext.ts";
import { getAccessedPropertyNames } from "src/components/Form/utils/getAccessedPropertyNames.ts";
import { unique } from "@exonn/utils";
import { getFieldConfig } from "src/components/Form/utils/getFieldConfig.ts";
import type { DisabledField } from "src/system/module.types.ts";

export const useIsFieldDisabled = ({
  name,
  control: controlProp,
}: {
  name: string;
  control?: Control<any>;
}) => {
  const { control: controlFromContext, getValues } = useFormContext();
  const control = controlProp ?? controlFromContext;
  const { disabled_fields, fields } = useFormFrameworkContext();
  const field = getFieldConfig(fields, name);

  const disabledFieldV2 = field?.disabled;
  const disabledFieldV1 = disabled_fields?.[name];
  const disabledField = disabledFieldV2 || disabledFieldV1;
  if (disabledFieldV1 && disabledFieldV2) {
    throw new Error(
      "you are mixing different approaches for disabling a field." +
        " Use either the old version e.g. 'disabled_fields'" +
        " or use the new version e.g. 'disabled' prop on a field",
    );
  }

  const namesToWatch =
    disabledField && typeof disabledField !== "boolean"
      ? getDisabledFieldNames({
          disabledField,
          values: getValues(),
          name,
        })
      : undefined;

  useWatch({
    // @ts-ignore
    name: namesToWatch,
    disabled: !disabledField,
    control,
  });

  if (typeof disabledField === "boolean") {
    return {
      forcedValue: undefined,
      isDisabled: disabledField,
      tooltip: undefined,
    };
  }

  if (typeof disabledField === "function") {
    return {
      forcedValue: undefined,
      isDisabled: disabledField(getValues()),
      tooltip: undefined,
    };
  }

  const forcedValue =
    disabledField && disabledField?.force_value?.(getValues());

  const isDisabled =
    disabledField && disabledField.disable_condition(getValues(), name);

  const tooltip = disabledField && disabledField.tooltip?.(getValues());

  return {
    forcedValue,
    isDisabled,
    tooltip,
  };
};

const getDisabledFieldNames = ({
  disabledField,
  values,
  name,
}: {
  disabledField: DisabledField<any>;
  name: string;
  values: any;
}): string[] => {
  if (typeof disabledField === "function") {
    return getAccessedPropertyNames({
      callback: (values) => disabledField(values),
      object: values,
    });
  }
  if (disabledField.watch_names) {
    return disabledField.watch_names as string[];
  }
  const namesAccessedInConditionCallback = getAccessedPropertyNames({
    callback: (values) => disabledField.disable_condition(values, name),
    object: values,
  });

  const namesAccessedInForceValueCallback = disabledField.force_value
    ? getAccessedPropertyNames({
        callback: disabledField.force_value,
        object: values,
      })
    : [];

  return unique([
    ...namesAccessedInConditionCallback,
    ...namesAccessedInForceValueCallback,
  ]);
};
